import React, { useState, useEffect } from 'react';
import { Globe, Headphones, Mic, BookOpen, PenTool, CheckCircle2, ArrowLeft, ArrowRight, Play, Flame } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
import { fetchLanguageBootstrap } from '../lib/languageClient';
import { TargetLanguage, LanguageDailyPlan } from '../types';

interface TodayLanguageCardProps {
  onNavigate: (tabId: string) => void;
  currentDate?: string;
}

export const TodayLanguageCard: React.FC<TodayLanguageCardProps> = ({ onNavigate, currentDate }) => {
  const { t, dir } = useLanguage();
  const [target, setTarget] = useState<TargetLanguage | null>(null);
  const [plan, setPlan] = useState<LanguageDailyPlan | null>(null);
  const [streak, setStreak] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    fetchLanguageBootstrap()
      .then(data => {
        if (!isMounted) return;
        if (data.targets && data.targets.length > 0) {
          const firstTarget = data.targets[0];
          setTarget(firstTarget);
          setStreak(data.profile?.streak_days || 0);

          let candidatePlan: LanguageDailyPlan | undefined;
          if (currentDate) {
            candidatePlan = data.dailyPlans.find(
              p => p.target_language_id === firstTarget.id && p.plan_date === currentDate
            );
          } else {
            candidatePlan = data.dailyPlans.find(p => p.target_language_id === firstTarget.id);
          }

          if (candidatePlan) {
            // Safely parse plan_items_json if needed
            if ((!candidatePlan.items || candidatePlan.items.length === 0) && candidatePlan.plan_items_json) {
              try {
                candidatePlan.items = JSON.parse(candidatePlan.plan_items_json);
              } catch {
                candidatePlan.items = [];
              }
            }
            setPlan(candidatePlan);
          } else {
            setPlan(null);
          }
        } else {
          setTarget(null);
          setPlan(null);
        }
      })
      .catch(err => {
        console.warn('Could not load language preview for dashboard:', err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [currentDate]);

  // Strict visibility constraint: Only show if there is an active target AND a scheduled daily plan with planned time
  if (loading || !target || !plan || (plan.total_planned_minutes <= 0 && (!plan.items || plan.items.length === 0))) {
    return null;
  }

  const completed = (plan.listening_completed_minutes || 0) +
    (plan.speaking_completed_minutes || 0) +
    (plan.reading_completed_minutes || 0) +
    (plan.writing_completed_minutes || 0);

  const total = plan.total_planned_minutes || 45;
  const pct = Math.min(100, Math.round((completed / total) * 100));

  return (
    <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-100/60 dark:from-gray-800 dark:via-gray-800 dark:to-emerald-950/30 rounded-2xl p-4 sm:p-5 border border-emerald-200/80 dark:border-emerald-800/40 shadow-sm transition-all hover:shadow-md">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 bg-emerald-600 text-white rounded-xl shadow-sm">
            <Globe className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm sm:text-base text-gray-900 dark:text-white">
              {t('dashLanguageLearningTitle', 'آموزش زبان: برنامه روزانه')}
            </h3>
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                {target.language_name} ({target.level})
              </span>
              {streak > 0 && (
                <span className="inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400 font-bold">
                  <Flame className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                  {streak} {t('days', 'روز')}
                </span>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={() => onNavigate('language-learning')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-colors shadow-sm"
        >
          <span>{t('langLearnStartPractice', 'شروع تمرین')}</span>
          {dir === 'rtl' ? <ArrowLeft className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Progress metrics */}
      <div className="space-y-1.5">
        <div className="flex justify-between items-center text-xs font-semibold text-gray-700 dark:text-gray-300">
          <span>{t('progress', 'پیشرفت امروز')}: {pct}%</span>
          <span className="text-emerald-700 dark:text-emerald-400 font-bold">
            {completed} / {total} {t('minutes', 'دقیقه')}
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* 4 skills pill row */}
      <div className="grid grid-cols-4 gap-1.5 mt-3 pt-3 border-t border-emerald-200/50 dark:border-gray-700">
        <div className="text-center p-1 rounded-lg bg-white/60 dark:bg-gray-700/60">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1">
            <Headphones className="w-3 h-3 text-blue-500" />
            <span className="truncate">{t('langLearnListening', 'شنیداری')}</span>
          </div>
          <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
            {plan.listening_completed_minutes}/{plan.listening_minutes}m
          </span>
        </div>

        <div className="text-center p-1 rounded-lg bg-white/60 dark:bg-gray-700/60">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1">
            <Mic className="w-3 h-3 text-purple-500" />
            <span className="truncate">{t('langLearnSpeaking', 'گفتاری')}</span>
          </div>
          <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
            {plan.speaking_completed_minutes}/{plan.speaking_minutes}m
          </span>
        </div>

        <div className="text-center p-1 rounded-lg bg-white/60 dark:bg-gray-700/60">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1">
            <BookOpen className="w-3 h-3 text-amber-500" />
            <span className="truncate">{t('langLearnReading', 'خوانداری')}</span>
          </div>
          <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
            {plan.reading_completed_minutes}/{plan.reading_minutes}m
          </span>
        </div>

        <div className="text-center p-1 rounded-lg bg-white/60 dark:bg-gray-700/60">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1">
            <PenTool className="w-3 h-3 text-emerald-500" />
            <span className="truncate">{t('langLearnWriting', 'نوشتاری')}</span>
          </div>
          <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
            {plan.writing_completed_minutes}/{plan.writing_minutes}m
          </span>
        </div>
      </div>
    </div>
  );
};
